import { Flex, Text } from '@servicetitan/anvil2';
import { TextField, PhoneInput, Checkbox } from '@servicetitan/anvil2-react-hook-form';
import { HtmlContent, useLegalDisclosure } from '@servicetitan/financing-utils-shared';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';
import { SaveFormDataToStore } from '../application/SaveFormDataToStore';
import { useCallback, createRef, useRef, useMemo } from 'react';
import { CustomerAuthChallengeStatus } from '@servicetitan/financing-api-client';
import { useCustomerAuthStartChallenge } from './useCustomerAuthStartChallenge';
import { Form, ImperativeForm } from '../common/Form';
import { ApplicationDataStore } from '../application';
import { useGreenSkyRecaptcha } from './useGreenSkyRecaptcha';
import ReCAPTCHA from 'react-google-recaptcha';
import { z } from 'zod';
import { schema } from '../external-application/schema';
import { StepWrapper } from './StepWrapper';
import { Buttons } from './Buttons';

interface RequestStepProps {
    onSuccess: (challengeData: CustomerAuthChallengeStatus) => void;
    onBack?: () => void;
}

export const RequestStep = ({ onSuccess, onBack }: RequestStepProps) => {
    const { setupData } = SetupDataStore.useStore();
    const { applicationData } = ApplicationDataStore.useStore();
    const formRef = createRef<ImperativeForm>();
    const recaptchaRef = useRef<ReCAPTCHA>(null);

    const phoneInfoButton = useLegalDisclosure({
        providerName: setupData?.provider,
        tenantId: setupData?.tenantId,
        category: 'phoneInfoButton',
    });

    const requestStepSchema = useMemo(() => {
        const baseSchema = schema.pick({
            email: true,
            primaryPhone: true,
        });

        if (phoneInfoButton) {
            return baseSchema.extend({
                phoneConsent: z.literal(true, {
                    errorMap: () => ({ message: 'You must agree to the phone consent' }),
                }),
            });
        }

        return baseSchema;
    }, [phoneInfoButton]);

    const emailInformation = useLegalDisclosure({
        providerName: setupData?.provider,
        tenantId: setupData?.tenantId,
        category: 'emailInformation',
    });

    const startChallengeMutation = useCustomerAuthStartChallenge({
        onSuccess,
        mutationKey: ['customerAuthStartChallenge'],
        recaptchaRef,
    });

    const handleContinue = useCallback(async () => {
        const isValid = await formRef.current?.submit();

        if (!isValid || startChallengeMutation.isPending) {
            return;
        }

        startChallengeMutation.mutate({
            phone: applicationData.externalApplication.primaryPhone!,
            email: applicationData.externalApplication.email!,
        });
    }, [
        applicationData.externalApplication.primaryPhone,
        applicationData.externalApplication.email,
        formRef,
        startChallengeMutation,
    ]);

    return (
        <StepWrapper>
            <div style={{ maxWidth: '514px' }}>
                <Form
                    onSubmit={() => {}}
                    formRef={formRef}
                    formData={applicationData.externalApplication}
                    schema={requestStepSchema}
                >
                    <SaveFormDataToStore fieldPrefix="externalApplication" />
                    <Flex gap="3" direction="column" style={{ width: '100%' }}>
                        <Text variant="headline" el="h2">
                            Secure your application.
                        </Text>
                        <Text variant="body" size="medium">
                            To secure your application, we need to verify that it's you. Enter your
                            mobile number and email to get started.
                        </Text>
                        <PhoneInput name="primaryPhone" label="Mobile Phone Number" required />
                        {phoneInfoButton && (
                            <Text subdued variant="body" size="small">
                                <Checkbox
                                    name="phoneConsent"
                                    label={
                                        <Text
                                            variant="body"
                                            size="small"
                                            subdued
                                            dangerouslySetInnerHTML={{
                                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                                __html: phoneInfoButton as string,
                                            }}
                                        />
                                    }
                                />
                            </Text>
                        )}
                        <TextField name="email" label="Email" inputMode="email" required />
                        {emailInformation && (
                            <Text subdued variant="body" size="small">
                                <HtmlContent content={emailInformation as string} />
                            </Text>
                        )}
                    </Flex>
                </Form>
            </div>
            {useGreenSkyRecaptcha(recaptchaRef)}
            <Buttons
                onBack={onBack}
                onContinue={handleContinue}
                isLoading={startChallengeMutation.isPending}
            />
        </StepWrapper>
    );
};
